import { useState, useEffect, useRef } from 'react';
import { useStudioStore } from '../../store/useStudioStore';
import { Trash2, Edit2, Plus } from 'lucide-react';

export const MIDILearn = () => {
  const { midiMappings, addMIDIMapping, removeMIDIMapping, updateMIDIMapping } = useStudioStore();
  const [isLearning, setIsLearning] = useState(false);
  const [midiAccess, setMidiAccess] = useState<MIDIAccess | null>(null);
  const [midiError, setMidiError] = useState<string | null>(null);
  const [learningParameter, setLearningParameter] = useState<string | null>(null);
  const [lastMIDIMessage, setLastMIDIMessage] = useState<{ cc: number; channel: number } | null>(null);
  
  // Available parameters to map
  const availableParameters = [
    { id: 'masterVolume', label: 'Master Volume' },
    { id: 'bpm', label: 'BPM' },
    ...useStudioStore.getState().channels.map((ch) => ({
      id: `channel-${ch.id}-volume`,
      label: `${ch.name} Volume`,
    })),
  ];

  // Request MIDI access on mount
  useEffect(() => {
    if (navigator.requestMIDIAccess) {
      navigator
        .requestMIDIAccess()
        .then((access) => {
          setMidiAccess(access);
          setMidiError(null);
          
          // Set up MIDI message listeners
          access.inputs.forEach((input) => {
            input.onmidimessage = handleMIDIMessage;
          });
        })
        .catch((error) => {
          console.error('MIDI access denied:', error);
          setMidiError('MIDI access denied. Please check your browser permissions.');
        });
    } else {
      setMidiError('Web MIDI API not supported in this browser.');
    }
  }, []);

  const handleMIDIMessage = (message: MIDIMessageEvent) => {
    const [status, cc, value] = message.data;
    const messageType = status & 0xf0;
    const channel = (status & 0x0f) + 1;

    // Only handle Control Change messages (0xB0)
    if (messageType === 0xb0) {
      setLastMIDIMessage({ cc, channel });
      
      if (isLearning && learningParameter) {
        // Create new mapping
        const parameter = availableParameters.find((p) => p.id === learningParameter);
        if (parameter) {
          addMIDIMapping({
            cc,
            channel,
            parameter: parameter.id,
            parameterLabel: parameter.label,
            min: 0,
            max: 1,
            curve: 'linear',
          });
          
          // Exit learning mode
          setIsLearning(false);
          setLearningParameter(null);
        }
      } else {
        // Apply existing mapping
        applyMapping(cc, channel, value);
      }
    }
  };

  const applyMapping = (cc: number, channel: number, value: number) => {
    const mapping = midiMappings.find((m) => m.cc === cc && m.channel === channel);
    if (!mapping) return;

    // Normalize MIDI value (0-127) to 0-1
    const normalizedValue = value / 127;

    // Apply curve transformation
    let transformedValue = normalizedValue;
    switch (mapping.curve) {
      case 'exponential':
        transformedValue = Math.pow(normalizedValue, 2);
        break;
      case 'logarithmic':
        transformedValue = Math.sqrt(normalizedValue);
        break;
      case 'linear':
      default:
        transformedValue = normalizedValue;
        break;
    }

    // Scale to parameter range
    const scaledValue = mapping.min + transformedValue * (mapping.max - mapping.min);

    // Update parameter in store
    updateParameter(mapping.parameter, scaledValue);
  };

  const updateParameter = (parameterId: string, value: number) => {
    const store = useStudioStore.getState();

    if (parameterId === 'masterVolume') {
      // TODO: Update master volume when audio engine is integrated
      console.log('Update master volume:', value);
    } else if (parameterId === 'bpm') {
      store.setBpm(Math.round(60 + value * 140)); // Map 0-1 to 60-200 BPM
    } else if (parameterId.startsWith('channel-')) {
      // Extract channel ID from parameter ID
      const channelId = parameterId.replace('channel-', '').replace('-volume', '');
      store.updateChannelVolume(channelId, value);
    }
  };

  const startLearning = (parameterId: string) => {
    setIsLearning(true);
    setLearningParameter(parameterId);
    setLastMIDIMessage(null);
  };

  const cancelLearning = () => {
    setIsLearning(false);
    setLearningParameter(null);
    setLastMIDIMessage(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-2">MIDI Learn</h3>
        <p className="text-sm text-zinc-400">
          Map MIDI controller knobs and faders to DAW parameters
        </p>
      </div>

      {/* MIDI Status */}
      {midiError ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400 text-sm">{midiError}</p>
        </div>
      ) : midiAccess ? (
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <p className="text-green-400 text-sm">
            ✓ MIDI Access Granted - {midiAccess.inputs.size} device(s) connected
          </p>
        </div>
      ) : (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4">
          <p className="text-zinc-400 text-sm">Requesting MIDI access...</p>
        </div>
      )}

      {/* Learning Mode Indicator */}
      {isLearning && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-400 font-medium">Learning Mode Active</p>
              <p className="text-sm text-zinc-400 mt-1">
                Move a MIDI controller to map it to:{' '}
                <span className="text-white">
                  {availableParameters.find((p) => p.id === learningParameter)?.label}
                </span>
              </p>
              {lastMIDIMessage && (
                <p className="text-xs text-zinc-500 mt-2">
                  Last message: CC {lastMIDIMessage.cc} on Channel {lastMIDIMessage.channel}
                </p>
              )}
            </div>
            <button
              onClick={cancelLearning}
              className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing Mappings */}
      <div>
        <h4 className="text-lg font-medium text-white mb-3">Active Mappings</h4>
        {midiMappings.length === 0 ? (
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-6 text-center">
            <p className="text-zinc-400">No MIDI mappings yet</p>
            <p className="text-sm text-zinc-500 mt-1">
              Click "Learn" next to a parameter to create a mapping
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {midiMappings.map((mapping) => (
              <div
                key={mapping.id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <p className="text-white font-medium">{mapping.parameterLabel}</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    CC {mapping.cc} • Channel {mapping.channel} • {mapping.curve} curve
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Range: {mapping.min.toFixed(2)} - {mapping.max.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      // TODO: Open edit dialog
                      console.log('Edit mapping:', mapping.id);
                    }}
                    className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                    title="Edit mapping"
                  >
                    <Edit2 className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button
                    onClick={() => removeMIDIMapping(mapping.id)}
                    className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete mapping"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available Parameters */}
      <div>
        <h4 className="text-lg font-medium text-white mb-3">Available Parameters</h4>
        <div className="space-y-2">
          {availableParameters.map((param) => {
            const existingMapping = midiMappings.find((m) => m.parameter === param.id);
            return (
              <div
                key={param.id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 flex items-center justify-between"
              >
                <p className="text-white">{param.label}</p>
                {existingMapping ? (
                  <span className="text-sm text-green-400">
                    ✓ Mapped to CC {existingMapping.cc}
                  </span>
                ) : (
                  <button
                    onClick={() => startLearning(param.id)}
                    disabled={isLearning}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-sm transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Learn
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
